import { useCallback, useEffect, useState } from 'react';
import { submitToolboxTalk } from '../api/client';
import { readToolboxOutbox, removeToolboxFromOutbox } from '../storage/toolboxOutbox';

export function useToolboxOutboxSync() {
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncError, setLastSyncError] = useState<string | null>(null);
  const [pendingSince, setPendingSince] = useState<string | null>(null);

  const syncOutbox = useCallback(async () => {
    const entries = await readToolboxOutbox();
    setPendingCount(entries.length);
    const oldest = entries.reduce<string | null>((acc, entry) => {
      if (!entry.queuedAt) return acc;
      if (!acc) return entry.queuedAt;
      return new Date(entry.queuedAt) < new Date(acc) ? entry.queuedAt : acc;
    }, null);
    setPendingSince(oldest);
    if (entries.length === 0 || !navigator.onLine) {
      return;
    }

    setIsSyncing(true);
    setLastSyncError(null);

    for (const entry of entries) {
      try {
        await submitToolboxTalk(entry.payload);
        if (entry.id != null) {
          await removeToolboxFromOutbox(entry.id);
        }
      } catch (error) {
        console.warn('Toolbox outbox sync failed', error);
        setLastSyncError((error as Error).message ?? 'Unable to sync queued toolbox talks');
        break;
      }
    }

    const remaining = await readToolboxOutbox();
    setPendingCount(remaining.length);
    const remainingOldest = remaining.reduce<string | null>((acc, entry) => {
      if (!entry.queuedAt) return acc;
      if (!acc) return entry.queuedAt;
      return new Date(entry.queuedAt) < new Date(acc) ? entry.queuedAt : acc;
    }, null);
    setPendingSince(remainingOldest);
    setIsSyncing(false);
  }, []);

  useEffect(() => {
    syncOutbox();
    const handleOnline = () => syncOutbox();
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [syncOutbox]);

  return { pendingCount, pendingSince, isSyncing, lastSyncError, syncOutbox };
}
